/**
 * How a subscription is labelled when the customer never named it.
 *
 * Subscriptions are unnamed by default (naming one is optional, in
 * /billing/settings/:id), so "Unnamed subscription" was the title of most rows
 * in the list — a label that identifies nothing and repeats itself down the
 * page. The fallback describes the CONTRACT instead, which is what actually
 * distinguishes one unnamed subscription from another: a committed one by its
 * term and the date it renews, an on-demand one by having no renewal at all.
 *
 * Lives here rather than in each page so the list, the checkout picker and
 * anything else labelling a subscription cannot drift apart.
 */

/**
 * Fields a fallback name is derived from. Typed structurally rather than as
 * `SubscriptionSummary` so the checkout picker's summaries and the detail
 * payload both fit with no cast.
 */
export interface NameableSubscription {
  name?: string | null
  commitment_type?: string | null
  term?: { kind: string } | null
  current_cycle_ends_at?: string | null
  /**
   * Optional, and only ever NARROWS the claim: a caller that does not send
   * them gets the label it always got. With them, a subscription is never
   * titled "renews 09/10" while a cancellation is scheduled for that very date,
   * nor while it bills no service (`inactive`) or has ended (`cancelled`).
   */
  status?: string | null
  pending_cancellation?: { effective_at?: string | null } | null
}

export interface NameOptions {
  /**
   * The label is shown where a service is about to be ADDED to the
   * subscription (the checkout picker): a parked one will renew on its cycle
   * end once it holds a service again, so the date stays.
   */
  prospective?: boolean
}

export function useSubscriptionDisplayName() {
  const { t } = useI18n()
  const { billingLocale } = useRuntimeConfig().public

  /** True when the title on screen is ours, not the customer's. */
  function isUnnamed(sub: NameableSubscription): boolean {
    return !sub.name?.trim()
  }

  function fallbackName(sub: NameableSubscription, options: NameOptions = {}): string {
    if (sub.commitment_type === 'ON_DEMAND') {
      return t('billing.subscriptionName.onDemand')
    }

    // The term phrase is translated per term rather than interpolated: "Plano
    // mensal" and "Monthly plan" put the adjective on opposite sides and
    // capitalise it differently.
    const termKey = sub.term?.kind
    const plan = termKey
      ? t(`billing.subscriptionName.plan.${termKey}`)
      : t('billing.subscriptionName.plan.UNKNOWN')

    if (!sub.current_cycle_ends_at) return plan
    const date = new Date(sub.current_cycle_ends_at).toLocaleDateString(billingLocale)
    if (sub.pending_cancellation) {
      const end = sub.pending_cancellation.effective_at
      return t('billing.subscriptionName.withEnd', {
        plan,
        date: end ? new Date(end).toLocaleDateString(billingLocale) : date,
      })
    }
    // Ended: nothing renews. Parked (`inactive`): nothing renews EITHER, as
    // things stand — except where the customer is choosing it to put a service
    // on (`prospective`), where that date is exactly when it will next renew.
    if (sub.status === 'cancelled') return plan
    if (sub.status === 'inactive' && !options.prospective) return plan
    return t('billing.subscriptionName.withRenewal', {
      plan,
      date: new Date(sub.current_cycle_ends_at).toLocaleDateString(billingLocale),
    })
  }

  function displayName(sub: NameableSubscription, options: NameOptions = {}): string {
    return sub.name?.trim() || fallbackName(sub, options)
  }

  return { displayName, isUnnamed }
}
